const STORAGE_KEY = 'glimraCookieConsent';
const LEGACY_KEY = 'cookieConsent';
const EVENT_NAME = 'glimra:consent-change';
const SCHEMA_VERSION = 1;

const readRaw = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== SCHEMA_VERSION) return null;
    return parsed;
  } catch (err) {
    return null;
  }
};

const writeRaw = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (err) {
    // localStorage can throw (quota exceeded, disabled, Safari private mode, etc.).
    // Fall back to an in-memory decision for this session so the UI can proceed.
    console.warn('ConsentStore: failed to persist consent', err);
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value }));
  } catch (err) {
    // Dispatching the change event is best-effort; persistence already succeeded.
    console.warn('ConsentStore: failed to dispatch consent event', err);
  }
  return true;
};

// Known deployment hosts → cookie domains we should clear cookies on.
// Localhost / unknown hosts only get the no-domain clear below.
const KNOWN_COOKIE_DOMAINS = {
  'subscriptions.glimra.se': ['.glimra.se', '.subscriptions.glimra.se'],
  'subscriptions-staging.glimra.se': ['.glimra.se', '.subscriptions-staging.glimra.se'],
  'juhlapesu-subscriptions.glimra.se': ['.glimra.se', '.juhlapesu-subscriptions.glimra.se'],
  'juhlapesu-subscriptions-staging.glimra.se': [
    '.glimra.se',
    '.juhlapesu-subscriptions-staging.glimra.se',
  ],
};

const clearMarketingCookies = () => {
  // Meta Pixel cookies. Clear on the current host (no domain attribute) and on
  // any known deployment domain we may have set them on.
  const cookieNames = ['_fbp', '_fbc'];
  const host = window.location.hostname;
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const knownDomains = KNOWN_COOKIE_DOMAINS[host] || [];
  cookieNames.forEach((name) => {
    document.cookie = `${name}=; ${expiry}; path=/`;
    knownDomains.forEach((domain) => {
      document.cookie = `${name}=; ${expiry}; path=/; domain=${domain}`;
    });
  });
};

class ConsentStore {
  constructor() {
    // Migrate legacy informational-only `cookieConsent` key by deleting it.
    // The old banner did not offer a Deny option, so the old "accept" cannot
    // be treated as a real opt-in for marketing cookies. Re-prompt the user.
    try {
      if (localStorage.getItem(LEGACY_KEY) !== null) {
        localStorage.removeItem(LEGACY_KEY);
      }
    } catch (err) {
      // ignore
    }
  }

  hasDecided = () => readRaw() !== null;

  getMarketingConsent = () => {
    const value = readRaw();
    return !!(value && value.marketing);
  };

  setMarketingConsent = (marketing) => {
    const value = {
      version: SCHEMA_VERSION,
      marketing: !!marketing,
      timestamp: new Date().toISOString(),
    };
    const persisted = writeRaw(value);
    if (persisted && !marketing) {
      clearMarketingCookies();
    }
    return persisted;
  };

  revoke = () => this.setMarketingConsent(false);

  subscribe = (handler) => {
    const listener = (event) => handler(event.detail);
    window.addEventListener(EVENT_NAME, listener);
    return () => window.removeEventListener(EVENT_NAME, listener);
  };
}

export const CONSENT_EVENT = EVENT_NAME;
export const REOPEN_BANNER_EVENT = 'glimra:consent-reopen';

const singleton = new ConsentStore();
export default singleton;
